//@ts-nocheck
import React, { useEffect, useRef, useState } from "react";
import { Button, Divider, Input, Popover, Select, Switch, message } from "antd";
import { useSearchParams } from "react-router-dom";
import { useSmartBack } from "../../utils/useSmartBack";
import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import Underline from "@tiptap/extension-underline";
import TextAlign from "@tiptap/extension-text-align";
import Link from "@tiptap/extension-link";
import Image from "@tiptap/extension-image";
import EmojiPicker from "emoji-picker-react";
import { TextStyle } from "@tiptap/extension-text-style";
import { Color } from "@tiptap/extension-color";
import {
  MdAdd,
  MdArrowBack,
  MdEmojiEmotions,
  MdFormatAlignCenter,
  MdFormatAlignLeft,
  MdFormatBold,
  MdFormatColorText,
  MdFormatItalic,
  MdFormatListBulleted,
  MdFormatUnderlined,
  MdInsertLink,
  MdInsertPhoto,
  MdMoreVert,
  MdOutlineNoteAdd,
  MdRedo,
  MdUndo,
} from "react-icons/md";
import {
  createEmailTemplate,
  getAppSettings,
  getEmailTemplates,
  renderEmailTemplate,
  updateAppSettings,
  updateEmailTemplate,
  uploadEmailImage,
  resolveEmailImageUrl,
} from "../../utils/API";
import PageLogoutButton from "../../Components/PageLogoutButton";
import NavigationDropdown from "../../Components/NavigationDropDown";

const PRESET_COLORS = [
  "#111827",
  "#374151",
  "#ef4444",
  "#f97316",
  "#eab308",
  "#10b981",
  "#3b82f6",
  "#2563eb",
  "#7c3aed",
  "#db2777",
];

const DEFAULT_TEMPLATE = {
  name: "",
  subjectTemplate: "",
  bodyTemplate: "<p></p>",
  senderEmail: "",
  isActive: true,
};

// Templates whose body is a fixed, complex HTML design maintained in backend
// code (e.g. the Alliance "Want List" layout). The rich-text editor would strip
// their table layout on save, so we show them as a read-only preview and never
// autosave them from this page. Edit these in the backend template file + seeder.
const LOCKED_SLUGS = new Set([
  "email-to-calibration-vendor",
  "vendor-submission-copy",
]);

const DEFAULT_MERGE_FIELDS = [
  "quoteId",
  "contactName",
  "contactEmail",
  "companyName",
  "createdDate",
  "quoteTotal",
  "customerComments",
  "assignedEmail",
  "notes",
  "quoteItemsTable",
  "quoteItemsTextSellOnly",
  "vendorFormUrl",
];

const normalize = (value) => String(value || "").trim();

// Email-template images are STORED with a host-relative src
// (/api/public/email-images/x.png) so a template stays portable between
// environments. The browser needs an absolute URL to actually display them,
// so we absolutize on load and re-relativize on save.
const EMAIL_IMAGE_PATH = "/api/public/email-images/";

const absolutizeEmailImages = (html) =>
  String(html || "").replace(
    /(<img\b[^>]*?\bsrc\s*=\s*)(["'])([^"']*?)\2/gi,
    (match, prefix, quote, src) =>
      src.startsWith(EMAIL_IMAGE_PATH)
        ? `${prefix}${quote}${resolveEmailImageUrl(src)}${quote}`
        : match,
  );

const relativizeEmailImages = (html) =>
  String(html || "").replace(
    /(<img\b[^>]*?\bsrc\s*=\s*)(["'])([^"']*?)\2/gi,
    (match, prefix, quote, src) => {
      const marker = src.indexOf(EMAIL_IMAGE_PATH);
      return marker >= 0
        ? `${prefix}${quote}${src.slice(marker)}${quote}`
        : match;
    },
  );

const slugify = (value) =>
  normalize(value)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "");

const EmailPage = () => {
  const smartBack = useSmartBack();
  const [searchParams, setSearchParams] = useSearchParams();
  const quoteId = normalize(searchParams.get("quoteId"));
  const templateSlugParam = normalize(searchParams.get("template"));
  const typeParam = normalize(searchParams.get("type"));
  const scopeParam = normalize(searchParams.get("scope"));
  const todayDateParam = normalize(searchParams.get("todayDate"));
  const fileInputRef = useRef(null);
  const [templates, setTemplates] = useState([]);
  // Sender email accounts configured on the Settings page. The first one is the
  // account used when a template has no explicit sender selected.
  const [emailAccounts, setEmailAccounts] = useState<string[]>([]);
  const [activeSlug, setActiveSlug] = useState(templateSlugParam);
  const [form, setForm] = useState(DEFAULT_TEMPLATE);
  const [isLoading, setIsLoading] = useState(false);
  const [hasLoadedInitialData, setHasLoadedInitialData] = useState(false);
  const [previewSubject, setPreviewSubject] = useState("");
  const [availableVariables, setAvailableVariables] = useState<string[]>([]);
  const autosaveTimerRef = useRef(null);
  const lastSavedSnapshotRef = useRef("");
  const editorRef = useRef(null);

  // Upload a File and insert it at the current caret position. Shared by the
  // toolbar picker, paste and drag-drop so all three persist identically.
  const uploadAndInsertImage = async (file) => {
    const activeEditor = editorRef.current;
    if (!file || !activeEditor) return;
    const hide = message.loading("Uploading image...", 0);
    try {
      const uploaded = await uploadEmailImage(file);
      const src = resolveEmailImageUrl(uploaded.url);
      activeEditor.chain().focus().setImage({ src }).run();
      setForm((prev) => ({ ...prev, bodyTemplate: activeEditor.getHTML() }));
      hide();
      message.success("Image uploaded");
    } catch (error) {
      hide();
      message.error(
        error?.response?.data?.message || "Failed to upload image",
      );
    }
  };

  const editor = useEditor({
    extensions: [
      StarterKit,
      Underline,
      TextAlign.configure({ types: ["heading", "paragraph"] }),
      Link.configure({
        openOnClick: false,
        HTMLAttributes: { class: "text-blue-600 underline" },
      }),
      Image.configure({
        HTMLAttributes: { class: "max-w-full rounded-lg h-auto" },
      }).extend({
        // Persist sizing/alignment attributes through save + reload so the
        // image comes back exactly as it was placed, not stripped to a bare src.
        addAttributes() {
          return {
            ...this.parent?.(),
            width: {
              default: null,
              renderHTML: (attrs) =>
                attrs.width ? { width: attrs.width } : {},
            },
            height: {
              default: null,
              renderHTML: (attrs) =>
                attrs.height ? { height: attrs.height } : {},
            },
            style: {
              default: null,
              renderHTML: (attrs) =>
                attrs.style ? { style: attrs.style } : {},
            },
          };
        },
      }),
      TextStyle,
      Color,
    ],
    content: DEFAULT_TEMPLATE.bodyTemplate,
    editorProps: {
      attributes: {
        class:
          "focus:outline-none min-h-[460px] p-8 text-gray-700 leading-relaxed text-[15px]",
      },
      // Pasted / dropped images must go through the upload endpoint too,
      // otherwise they land as data: URIs that never persist.
      handlePaste: (_view, event) => {
        const files = Array.from(event.clipboardData?.files || []).filter((f) =>
          f.type.startsWith("image/"),
        );
        if (!files.length) return false;
        event.preventDefault();
        files.forEach((file) => uploadAndInsertImage(file));
        return true;
      },
      handleDrop: (_view, event) => {
        const files = Array.from(event.dataTransfer?.files || []).filter((f) =>
          f.type.startsWith("image/"),
        );
        if (!files.length) return false;
        event.preventDefault();
        files.forEach((file) => uploadAndInsertImage(file));
        return true;
      },
    },
  });

  // Keep the ref pointing at the live editor for the handlers above.
  editorRef.current = editor;

  const loadTemplates = async (preferredSlug) => {
    try {
      setIsLoading(true);
      const data = await getEmailTemplates();
      setTemplates(data);

      const slugToUse =
        preferredSlug ||
        templateSlugParam ||
        activeSlug ||
        normalize(data?.[0]?.slug);

      if (!slugToUse) {
        setForm(DEFAULT_TEMPLATE);
        setPreviewSubject("");
        if (editor) editor.commands.setContent(DEFAULT_TEMPLATE.bodyTemplate);
        setHasLoadedInitialData(true);
        return;
      }

      const matched = data.find((item) => item.slug === slugToUse) || data[0];
      if (!matched) return;

      // Stored src values are relative; make them absolute so uploaded images
      // actually render in the editor after a refresh.
      const loadedBody = absolutizeEmailImages(matched.bodyTemplate || "<p></p>");

      setActiveSlug(matched.slug);
      setForm({
        id: matched.id,
        name: matched.name || "",
        slug: matched.slug || "",
        subjectTemplate: matched.subjectTemplate || "",
        bodyTemplate: loadedBody,
        senderEmail: matched.senderEmail || "",
        isActive: Boolean(matched.isActive),
      });
      // Snapshot the *stored* form so an absolutize/relativize cycle alone
      // never looks like an edit and re-triggers autosave.
      lastSavedSnapshotRef.current = JSON.stringify({
        name: matched.name || "",
        subjectTemplate: matched.subjectTemplate || "",
        bodyTemplate: relativizeEmailImages(loadedBody),
        senderEmail: matched.senderEmail || "",
        isActive: Boolean(matched.isActive),
      });

      if (editor) {
        editor.commands.setContent(loadedBody);
      }

      if (quoteId) {
        const preview = await renderEmailTemplate(matched.slug, {
          quoteId,
          ...(typeParam ? { type: typeParam, listType: typeParam } : {}),
          ...(scopeParam ? { scope: scopeParam } : {}),
          ...(todayDateParam ? { todayDate: todayDateParam } : {}),
          ...(typeParam || scopeParam ? { includeWtb: true } : {}),
        });
        setAvailableVariables(Object.keys(preview?.variables || {}));
        setPreviewSubject(preview?.rendered?.subject || matched.subjectTemplate || "");
      } else if (typeParam || scopeParam) {
        const preview = await renderEmailTemplate(matched.slug, {
          ...(typeParam ? { type: typeParam, listType: typeParam } : {}),
          ...(scopeParam ? { scope: scopeParam } : {}),
          ...(todayDateParam ? { todayDate: todayDateParam } : {}),
          includeWtb: true,
        });
        setAvailableVariables(Object.keys(preview?.variables || {}));
        setPreviewSubject(preview?.rendered?.subject || matched.subjectTemplate || "");
      } else {
        setAvailableVariables([]);
        setPreviewSubject(matched.subjectTemplate || "");
      }
      setHasLoadedInitialData(true);
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to load email templates");
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!editor) return;
    loadTemplates(templateSlugParam);
  }, [editor]);

  // Populate the "Send from" dropdown with the email IDs added on the Settings
  // page. Non-fatal if it fails — the field just stays empty (= default sender).
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const settings = await getAppSettings();
        const list = Array.isArray(settings?.emailAccounts)
          ? settings.emailAccounts
              .map((a) => normalize(a?.email))
              .filter(Boolean)
          : [];
        if (!cancelled) {
          setEmailAccounts(list);
        }
      } catch {
        if (!cancelled) setEmailAccounts([]);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    if (!templateSlugParam) return;
    setActiveSlug(templateSlugParam);
  }, [templateSlugParam]);

  const handleSelectTemplate = async (slug) => {
    setActiveSlug(slug);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("template", slug);
    if (quoteId) nextParams.set("quoteId", quoteId);
    setSearchParams(nextParams, { replace: true });
    await loadTemplates(slug);
  };

  const handleNewTemplate = () => {
    const templateName = normalize(window.prompt("Enter template name"));
    if (!templateName) {
      return;
    }
    const generatedSlug = slugify(templateName);
    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("template", generatedSlug);
    setSearchParams(nextParams, { replace: true });
    setActiveSlug("");
    setPreviewSubject("");
    setForm({
      ...DEFAULT_TEMPLATE,
      name: templateName,
      slug: generatedSlug,
      bodyTemplate: "<p></p>",
    });
    lastSavedSnapshotRef.current = "";
    setHasLoadedInitialData(true);
    if (editor) {
      editor.commands.setContent("<p></p>");
    }
  };

  const persistTemplate = async () => {
    const payload = {
      ...form,
      // Store host-relative image URLs so the template stays portable.
      bodyTemplate: relativizeEmailImages(
        editor?.getHTML?.() || form.bodyTemplate || "<p></p>",
      ),
    };
    const snapshot = JSON.stringify({
      name: payload.name,
      subjectTemplate: payload.subjectTemplate,
      bodyTemplate: payload.bodyTemplate,
      senderEmail: payload.senderEmail || "",
      isActive: payload.isActive,
    });

    if (!normalize(payload.name)) {
      message.warning("Template name is required");
      return;
    }

    if (snapshot === lastSavedSnapshotRef.current) {
      return;
    }

    try {
      const isNew = !activeSlug;
      const saved = isNew
        ? await createEmailTemplate(payload)
        : await updateEmailTemplate(activeSlug, payload);

      lastSavedSnapshotRef.current = snapshot;

      // Update URL params to reflect the saved slug
      const nextParams = new URLSearchParams(searchParams);
      nextParams.set("template", saved.slug);
      if (quoteId) nextParams.set("quoteId", quoteId);
      setSearchParams(nextParams, { replace: true });

      if (isNew) {
        // New template: full reload needed to get server-assigned id/slug
        await loadTemplates(saved.slug);
      } else {
        // Existing template: just sync local state — do NOT call setContent
        // on the editor, which would reset cursor position to end of document.
        setActiveSlug(saved.slug);
        setForm((prev) => ({
          ...prev,
          id: saved.id ?? prev.id,
          slug: saved.slug ?? prev.slug,
        }));
      }
    } catch (error) {
      message.error(error?.response?.data?.message || "Failed to save template");
    }
  };

  useEffect(() => {
    if (!editor || !hasLoadedInitialData) return;
    if (!normalize(form.name)) return;
    // Locked templates are read-only on this page — never autosave them.
    if (LOCKED_SLUGS.has(normalize(activeSlug))) return;

    if (autosaveTimerRef.current) {
      clearTimeout(autosaveTimerRef.current);
    }

    autosaveTimerRef.current = setTimeout(() => {
      persistTemplate();
    }, 700);

    return () => {
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
    };
  }, [
    form.name,
    form.subjectTemplate,
    form.senderEmail,
    form.isActive,
    activeSlug,
    hasLoadedInitialData,
    editor,
  ]);

  useEffect(() => {
    if (!editor || !hasLoadedInitialData) return;
    if (LOCKED_SLUGS.has(normalize(activeSlug))) return;
    const onUpdate = () => {
      if (!normalize(form.name)) return;
      if (autosaveTimerRef.current) {
        clearTimeout(autosaveTimerRef.current);
      }
      autosaveTimerRef.current = setTimeout(() => {
        persistTemplate();
      }, 700);
    };

    editor.on("update", onUpdate);
    return () => {
      editor.off("update", onUpdate);
    };
  }, [editor, form.name, form.subjectTemplate, form.senderEmail, form.isActive, activeSlug, hasLoadedInitialData]);

  // Locked templates never autosave from this page, so persist the sender
  // choice on its own with a partial update (other fields stay untouched).
  const handleSenderEmailChange = (value) => {
    const nextSender = value || "";
    setForm((prev) => ({ ...prev, senderEmail: nextSender }));
    if (activeSlug && LOCKED_SLUGS.has(normalize(activeSlug))) {
      updateEmailTemplate(activeSlug, { senderEmail: nextSender })
        .then(() => {
          lastSavedSnapshotRef.current = "";
          message.success("Sender updated");
        })
        .catch((error) =>
          message.error(
            error?.response?.data?.message || "Failed to update sender",
          ),
        );
    }
  };

  const addMergeField = (field) => {
    if (!editor) return;
    editor
      .chain()
      .focus()
      .insertContent(
        `<span style="color:#2563eb;font-weight:500;">{${field}}</span> `
      )
      .run();
  };

  if (!editor) return null;

  const isLocked = LOCKED_SLUGS.has(normalize(activeSlug));

  const mergeFields = Array.from(
    new Set([...DEFAULT_MERGE_FIELDS, ...availableVariables]),
  );

  const colorPickerContent = (
    <div className="grid grid-cols-5 gap-2 p-1">
      {PRESET_COLORS.map((color) => (
        <div
          key={color}
          onClick={() => editor.chain().focus().setColor(color).run()}
          className="h-6 w-6 cursor-pointer rounded-full border border-gray-200"
          style={{ backgroundColor: color }}
        />
      ))}
    </div>
  );

  return (
    <div className="min-h-screen bg-[#f7f7f7] p-6">
      <div className="flex items-center justify-between mb-2">
        <div className="flex items-center gap-2">
          <Button icon={<MdArrowBack />} onClick={smartBack}>
            Back
          </Button>
          <NavigationDropdown />
        </div>
        <PageLogoutButton />
      </div>

      <div className="mx-auto flex max-w-7xl gap-6">
        <div className="w-80 rounded-3xl border border-gray-200 bg-white p-5 shadow-sm">
          <div className="mb-4 flex items-center justify-between">
            <p className="text-sm font-semibold text-gray-700">Templates</p>
            <Button icon={<MdOutlineNoteAdd />} onClick={handleNewTemplate}>
              New
            </Button>
          </div>
          <div className="space-y-2">
            {templates.map((template) => (
              <button
                key={template.id}
                type="button"
                onClick={() => handleSelectTemplate(template.slug)}
                className={`w-full rounded-2xl px-4 py-3 text-left text-sm transition ${
                  activeSlug === template.slug
                    ? "bg-[#e8eefc] font-medium text-[#004aad]"
                    : "bg-[#f7f7f7] text-gray-600 hover:bg-[#eef2ff]"
                }`}
              >
                <div>{template.name}</div>
                <div className="mt-1 text-xs text-gray-400">{template.slug}</div>
              </button>
            ))}
          </div>
        </div>

        <div className="flex-1 rounded-3xl border border-gray-200 bg-white p-6 shadow-sm">
          <div className="mb-5 flex items-start justify-between gap-4">
            <div>
              <h1 className="text-xl text-gray-800">
                {normalize(form.name) || "Email Template"}
              </h1>
              <p className="text-sm text-gray-500">
                {quoteId
                  ? `Previewing template for Quote #${quoteId}`
                  : "Manage template content and merge fields"}
              </p>
            </div>
          </div>

          <div className="mb-6 rounded-2xl border border-gray-200 bg-[#fafafa] p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-[1fr_auto] md:items-end">
              <div>
                <p className="mb-2 text-xs font-semibold uppercase text-gray-500">
                  Subject Template
                </p>
                <Input
                  value={form.subjectTemplate}
                  disabled={isLocked}
                  onChange={(e) =>
                    setForm((prev) => ({ ...prev, subjectTemplate: e.target.value }))
                  }
                  placeholder="Your Quote #{quoteId} from {companyName}"
                  className="h-11 rounded-xl border-[#d7deea] shadow-none hover:border-[#9db7e5] focus:border-[#004aad]"
                />
                {quoteId ? (
                  <div className="mt-2 rounded-xl border border-blue-100 bg-blue-50 px-3 py-2 text-xs text-blue-700">
                    Preview: {previewSubject || "-"}
                  </div>
                ) : null}
              </div>
              <div className="flex items-center gap-3 md:justify-end">
                <span className="text-xs font-semibold uppercase text-gray-500">
                  Active
                </span>
                <Switch
                  checked={Boolean(form.isActive)}
                  disabled={isLocked}
                  onChange={(checked) =>
                    setForm((prev) => ({ ...prev, isActive: checked }))
                  }
                />
              </div>
            </div>

            <div className="mt-4">
              <p className="mb-2 text-xs font-semibold uppercase text-gray-500">
                Send From
              </p>
              <Select
                value={form.senderEmail || undefined}
                onChange={handleSenderEmailChange}
                allowClear
                placeholder={
                  emailAccounts.length
                    ? "Use default sender account"
                    : "No email accounts configured in Settings"
                }
                options={emailAccounts.map((email) => ({
                  label: email === emailAccounts[0] ? `${email} (default)` : email,
                  value: email,
                }))}
                className="w-full md:w-96"
                style={{ height: 44 }}
              />
              <p className="mt-1 text-xs text-gray-400">
                This template's emails are sent from this address. Leave blank to
                use the default account. Manage the list in Settings.
              </p>
            </div>
          </div>

          {isLocked ? (
            <div className="overflow-hidden rounded-3xl border border-gray-200">
              <div className="flex items-center justify-between border-b border-gray-100 bg-[#f9fafb] px-6 py-4">
                <p className="text-sm font-medium text-gray-600">
                  Fixed design preview (read-only)
                </p>
                <span className="rounded-full bg-amber-50 px-3 py-1 text-xs font-medium text-amber-700">
                  Locked · edit in backend template
                </span>
              </div>
              <div className="max-h-[70vh] overflow-auto bg-[#f2f4f7] p-6">
                <div
                  className="mx-auto max-w-[760px]"
                  dangerouslySetInnerHTML={{
                    __html: form.bodyTemplate || "<p></p>",
                  }}
                />
              </div>
            </div>
          ) : (
          <div className="overflow-hidden rounded-3xl border border-gray-200">
            <div className="flex items-center justify-between border-b border-gray-100 bg-[#f9fafb] px-6 py-4">
              <div className="flex items-center gap-5 text-xl text-gray-500">
                <div className="flex items-center gap-4">
                  <MdFormatBold
                    onClick={() => editor.chain().focus().toggleBold().run()}
                    className="cursor-pointer"
                  />
                  <MdFormatItalic
                    onClick={() => editor.chain().focus().toggleItalic().run()}
                    className="cursor-pointer"
                  />
                  <MdFormatUnderlined
                    onClick={() => editor.chain().focus().toggleUnderline().run()}
                    className="cursor-pointer"
                  />
                  <Popover content={colorPickerContent} trigger="click">
                    <MdFormatColorText className="cursor-pointer" />
                  </Popover>
                </div>
                <Divider type="vertical" className="h-6 border-gray-300" />
                <div className="flex items-center gap-4">
                  <MdFormatAlignLeft
                    onClick={() => editor.chain().focus().setTextAlign("left").run()}
                    className="cursor-pointer"
                  />
                  <MdFormatAlignCenter
                    onClick={() => editor.chain().focus().setTextAlign("center").run()}
                    className="cursor-pointer"
                  />
                  <MdFormatListBulleted
                    onClick={() => editor.chain().focus().toggleBulletList().run()}
                    className="cursor-pointer"
                  />
                </div>
                <Divider type="vertical" className="h-6 border-gray-300" />
                <div className="flex items-center gap-4">
                  <MdInsertLink className="cursor-pointer" />
                  <input
                    type="file"
                    ref={fileInputRef}
                    className="hidden"
                    accept="image/*"
                    onChange={async (e) => {
                      const input = e.target;
                      const file = input.files?.[0];
                      // Clear immediately so picking the same file twice re-fires.
                      input.value = "";
                      if (file) await uploadAndInsertImage(file);
                    }}
                  />
                  <MdInsertPhoto
                    className="cursor-pointer"
                    onClick={() => fileInputRef.current?.click()}
                  />
                  <Popover
                    content={
                      <EmojiPicker
                        onEmojiClick={(event) =>
                          editor.chain().focus().insertContent(event.emoji).run()
                        }
                      />
                    }
                    trigger="click"
                  >
                    <MdEmojiEmotions className="cursor-pointer" />
                  </Popover>
                  <MdAdd className="cursor-pointer" />
                </div>
              </div>
              <div className="flex items-center gap-4 text-xl text-gray-400">
                <MdUndo
                  onClick={() => editor.chain().focus().undo().run()}
                  className="cursor-pointer"
                />
                <MdRedo
                  onClick={() => editor.chain().focus().redo().run()}
                  className="cursor-pointer"
                />
                <MdMoreVert className="cursor-pointer" />
              </div>
            </div>

            <EditorContent editor={editor} />
          </div>
          )}

          {isLocked ? null : (
          <div className="mt-6 rounded-2xl border border-gray-200 bg-[#fafafa] p-4">
            <p className="mb-3 text-sm font-medium text-gray-600">Merge Fields</p>
            <div className="flex flex-wrap gap-3 text-sm">
              {mergeFields.map((field) => (
                <button
                  key={field}
                  type="button"
                  onClick={() => addMergeField(field)}
                  className="rounded-full border border-blue-200 bg-blue-50 px-3 py-1 text-blue-700"
                >
                  {`{${field}}`}
                </button>
              ))}
            </div>
          </div>
          )}

          {isLoading ? (
            <p className="mt-4 text-sm text-gray-500">Loading templates...</p>
          ) : null}
        </div>
      </div>
    </div>
  );
};

export default EmailPage;
